import { db } from '../firebase';
import {
  collection, getDocs, getDoc,
  doc, query, orderBy, serverTimestamp, where, limit, runTransaction
} from 'firebase/firestore';
import { logService } from './logService';
import { maskIfViewer } from '../utils/permissions';
import { addDoc, updateDoc, deleteDoc } from './guardedDb';

const AS_COLLECTION = 'E2B_AS_RECORDS';
const PART_COLLECTION = 'E2B_PARTS';
const HISTORY_COLLECTION = 'E2B_PART_HISTORY';

// 정산 품목 하나에 해당하는 부품 마스터를 찾는다(품번이 있으면 품번 우선, 없으면 이름으로 추측).
const findMasterPart = (sortedMaster, item) => {
  if (item.partNo) {
    const byNo = sortedMaster.find(p => String(p.partNo) === String(item.partNo) || String(p.partCode) === String(item.partNo));
    if (byNo) return byNo;
  }
  if (item.taskName) {
    const keyword = String(item.taskName).toLowerCase().replace(/\s+/g, '');
    return sortedMaster.find(p => {
      const pName = String(p.partName || '').toLowerCase().replace(/\s+/g, '');
      const pNo = String(p.partNo || '').toLowerCase().replace(/\s+/g, '');
      return (pName && keyword.includes(pName)) || (pNo && keyword.includes(pNo));
    });
  }
  return null;
};

// 재고를 delta 만큼 뺀다(delta 가 음수면 되돌린다).
// 다른 사람이 같은 순간에 입고/출고해도 값이 어긋나지 않도록 트랜잭션으로 '지금 값'을 다시 읽어 계산한다.
const applyStockDelta = async (partId, delta, settleDate, record) => {
  const partRef = doc(db, PART_COLLECTION, partId);
  let name = partId, prev = 0, next = 0;

  await runTransaction(db, async (tx) => {
    const snap = await tx.get(partRef);
    if (!snap.exists()) throw new Error('부품을 찾을 수 없습니다: ' + partId);
    const data = snap.data();
    name = data.partName || partId;
    prev = Number(data.stock || 0);
    next = prev - delta;
    tx.update(partRef, { stock: next, updatedAt: serverTimestamp() });
  });

  await addDoc(collection(db, HISTORY_COLLECTION), {
    partId,
    date: settleDate,
    type: delta > 0 ? '출고' : '출고취소',
    qty: `${delta > 0 ? '-' : '+'}${Math.abs(delta)}`,
    prevStock: prev,
    currentStock: next,
    user: (record && record.mechanic) || '시스템',
    note: `[AS정산${delta > 0 ? '출고' : '되돌림'}] ${(record && (record.carNumber || record.customerName)) || ''}`,
    createdAt: serverTimestamp()
  });

  return { name, shortage: next < 0 };
};

export const asService = {
  // 1. 모든 AS 기록 불러오기
  getRecords: async (max) => {
    try {
      // max 를 주면 최신순으로 그 개수만 받는다(모바일 대량 이력 다운로드 방지). 없으면 전체(기존 동작).
      const base = collection(db, AS_COLLECTION);
      const q = max
        ? query(base, orderBy("createdAt", "desc"), limit(max))
        : query(base, orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      // 외부 열람(VIEWER) 계정에게는 여기서 고객 이름을 가린다 — 모든 화면·인쇄·엑셀에 자동 적용된다.
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, customerName: maskIfViewer(doc.data().customerName) }));
    } catch (e) {
      console.error("AS 기록 로드 실패:", e);
      return [];
    }
  },

  // 2. 신규 AS 접수 저장
  saveRegistration: async (data) => {
    try {
      const { id: _, ...pureData } = data;
      await addDoc(collection(db, AS_COLLECTION), {
        ...pureData,
        status: pureData.status || '접수',   // 폼에서 고른 '예약' 등을 덮어쓰지 않는다
        createdAt: serverTimestamp(),
        parts: [],
        totalCost: 0
      });
      logService.record({
        module: 'AS관리', action: 'CREATE',
        target: `차량 [${pureData.carNumber || '미지정'}]`,
        details: `${pureData.serviceCategory || '정비'} 신규 접수 (고객: ${pureData.customerName || '-'}, 담당: ${pureData.mechanic || '미지정'}, 접수일: ${pureData.asDate || '-'})`
      });
    } catch (e) { throw e; }
  },

  // 3. 기록 수정
  updateRecord: async (updatedData) => {
    try {
      const { id, ...pureData } = updatedData;
      if (!id) throw new Error("수정 대상을 찾을 수 없습니다.");
      const docRef = doc(db, AS_COLLECTION, id);
      await updateDoc(docRef, { ...pureData, updatedAt: serverTimestamp() });
      logService.record({
        module: 'AS관리', action: 'UPDATE',
        target: `차량 [${pureData.carNumber || id}]`,
        details: `정비 내역 수정 (상태: ${pureData.status || '-'}, 고객: ${pureData.customerName || '-'}${pureData.totalCost ? `, 합계: ₩${Number(pureData.totalCost).toLocaleString()}` : ''})`
      });
    } catch (e) { throw e; }
  },

  // 4. 기록 삭제
  remove: async (id) => {
    try {
      if (!id) return;
      // 무엇이 지워졌는지 남기기 위해 삭제 전에 차량/고객을 확보한다(감사 목적).
      let label = id;
      let who = '-';
      try {
        const snap = await getDoc(doc(db, AS_COLLECTION, id));
        if (snap.exists()) { label = snap.data().carNumber || id; who = snap.data().customerName || '-'; }
      } catch (_) { /* 확보 실패해도 삭제는 진행 */ }

      await deleteDoc(doc(db, AS_COLLECTION, id));
      logService.record({
        module: 'AS관리', action: 'DELETE',
        target: `차량 [${label}]`,
        details: `정비 내역 삭제 (고객: ${who})`
      });
    } catch (e) { throw e; }
  },

  // 🚀 5. 부품 재고 차감 — '이미 빼간 양'과 비교해 차이만 적용한다.
  //
  // 왜 이렇게 바꿨나(2026-09-17 감사 후속):
  //  - 예전엔 차감하기 '전에' 반영완료 표시를 먼저 찍어, 중간에 실패하면 영원히 차감되지 않았다.
  //  - 화면이 들고 있던 옛 재고값으로 덮어써서, 그 사이 누가 입고하면 그 입고분이 사라졌다.
  //  - 재정산(부품 추가·삭제)에서는 재고를 아예 건드리지 않아 장부가 어긋났다.
  //  이제 stockApplied(이 건이 지금까지 빼간 양)를 남겨두고, 그 차이만 더하거나 되돌린다.
  //  → 같은 건을 몇 번 저장해도 재고는 항상 맞는다.
  //
  // 반환값 { unmatched, negatives } 는 호출한 화면에서 사용자에게 알려 준다.
  settleRecordWithParts: async (updatedRecord, repairParts, settleDate, allMasterParts = []) => {
    const { id, ...pureData } = updatedRecord;
    if (!id) throw new Error("수정 대상을 찾을 수 없습니다.");
    const asDocRef = doc(db, AS_COLLECTION, id);

    // 1) 부품 마스터 확보(매칭용). 이름이 긴 것부터 비교해야 정확도가 올라간다.
    let master = allMasterParts;
    if (!master || master.length === 0) {
      const snap = await getDocs(collection(db, PART_COLLECTION));
      master = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    }
    const sortedMaster = [...master].sort((a, b) => (String(b.partName || '').length) - (String(a.partName || '').length));

    // 2) 이번 정산 기준으로 '빠져 있어야 할' 양
    const desired = {};
    const unmatched = [];
    for (const item of (repairParts || [])) {
      const qty = Number(item.qty) || 0;
      if (qty <= 0) continue;                                  // 수량 0 은 차감하지 않는다
      const target = findMasterPart(sortedMaster, item);
      if (!target || !target.id) { unmatched.push(item.taskName || '이름 없는 품목'); continue; }
      desired[target.id] = (desired[target.id] || 0) + qty;
    }

    // 3) 지금까지 빠져나간 양.
    //    예전 방식으로 이미 차감된 건은 기록이 없으므로, '지금 품목만큼은 이미 빠졌다'고 보아 중복 차감을 막는다.
    const already = (updatedRecord.stockApplied && typeof updatedRecord.stockApplied === 'object')
      ? updatedRecord.stockApplied
      : (updatedRecord.isStockReflected ? { ...desired } : {});

    // 4) 차이만 반영
    const negatives = [];
    const ids = Array.from(new Set([...Object.keys(desired), ...Object.keys(already)]));
    for (const partId of ids) {
      const delta = (Number(desired[partId]) || 0) - (Number(already[partId]) || 0);
      if (delta === 0) continue;
      const res = await applyStockDelta(partId, delta, settleDate, updatedRecord);
      if (res && res.shortage) negatives.push(res.name);
    }

    // 5) 재고 반영이 끝난 뒤에 원장을 기록한다(중간 실패 시 다시 시도할 수 있도록 순서를 뒤로 뺐다).
    await updateDoc(asDocRef, {
      ...pureData,
      stockApplied: desired,
      isStockReflected: true,
      updatedAt: serverTimestamp()
    });

    logService.record({
      module: 'AS관리', action: 'UPDATE',
      target: `차량 [${pureData.carNumber || id}]`,
      details: `정산 처리 (고객: ${pureData.customerName || '-'}, 정산일: ${settleDate || '-'}, 합계: ₩${Number(pureData.totalCost || 0).toLocaleString()}${Number(pureData.discount) ? `, 할인: ₩${Number(pureData.discount).toLocaleString()}` : ''}, 부품 ${(repairParts || []).length}건${unmatched.length ? `, 미매칭 ${unmatched.length}건` : ''})`
    });

    return { unmatched, negatives };
  }
};
